import config from './config.json'
import AgoraRTC from 'agora-rtc-sdk-ng'
import VirtualBackgroundExtension from 'agora-extension-virtual-background'

/**
 * Gerencia as funcionalidades do SDK da Agora.
 * @param {function} eventsCallback - Função de callback para os eventos da Agora.
 * @returns {Object} Funções do gerenciador da Agora.
 */
const AgoraManager = async (eventsCallback) => {
  let agoraEngine = null

  // ! Registro da extensão de fundo virtual
  const virtualBackgroundExtension = new VirtualBackgroundExtension()
  if (!virtualBackgroundExtension.checkCompatibility()) {
    console.error('Sem suporte para fundo virtual!')
  }
  AgoraRTC.registerExtensions([virtualBackgroundExtension])
  let virtualBackgroundProcessor = null

  /**
   * Configura o engine da Agora.
   * @async
   */
  const setupAgoraEngine = async () => {
    agoraEngine = new AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
  }
  await setupAgoraEngine()

  /**
   * Obtém a instância do engine da Agora.
   * @returns {Object} Instância do engine da Agora.
   */
  const getAgoraEngine = () => {
    return agoraEngine
  }

  // ! Event Listeners
  // Escuta os eventos de usuário da Agora
  agoraEngine.on('user-left', async (user, mediaType) => {
    eventsCallback('user-left', user, mediaType)
  })
  agoraEngine.on('user-joined', async (user, mediaType) => {
    eventsCallback('user-joined', user, mediaType)
  })
  agoraEngine.on('stream-added', async (user, mediaType) => {
    eventsCallback('stream-added', user, mediaType)
  })
  agoraEngine.on('stream-subscribed', async (user, mediaType) => {
    eventsCallback('stream-subscribed', user, mediaType)
  })
  agoraEngine.on('user-published', async (user, mediaType) => {
    await agoraEngine.subscribe(user, mediaType)
    eventsCallback('user-published', user, mediaType)
  })
  agoraEngine.on('user-unpublished', (user) => {
    eventsCallback('user-unpublished', user)
  })

  /**
   * Entra em uma sala da Agora e publica o vídeo local.
   * @param {HTMLElement} appendLocalPlayerContainer - Elemento HTML onde o vídeo será anexado.
   * @param {HTMLElement} localPlayerContainer - Container de vídeo local.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @param {Object} joinParameters - Parâmetros de entrada na sala.
   * @async
   */
  const join = async (appendLocalPlayerContainer, localPlayerContainer, channelParameters, joinParameters) => {
    console.warn('[Agora manager]', 'Start Join', joinParameters)
    await agoraEngine.join(
      joinParameters.appId, 
      joinParameters.channelName, 
      joinParameters.token, 
      `funId_${joinParameters.funId}`
    )

    // Cria faixas de áudio e vídeo locais
    channelParameters.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack()
    channelParameters.localVideoTrack = await AgoraRTC.createCameraVideoTrack()

    // Define o tamanho do vídeo local
    localPlayerContainer.id = `local_video`
    localPlayerContainer.style.width = '100%'
    localPlayerContainer.style.height = '100%'
    appendLocalPlayerContainer.append(localPlayerContainer)

    // Publica faixas de áudio e vídeo
    await getAgoraEngine().publish([channelParameters.localAudioTrack, channelParameters.localVideoTrack])

    // Toca o vídeo local
    channelParameters.localVideoTrack.play(localPlayerContainer)
  }

  /**
   * Sai da sala da Agora e fecha as faixas de áudio e vídeo locais.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @async
   */
  const leave = async (channelParameters) => {
    if (!channelParameters.localAudioTrack || !channelParameters.localVideoTrack) {
      return
    }
    channelParameters.localAudioTrack.close()
    channelParameters.localVideoTrack.close()
    await agoraEngine.leave()
  }

  /**
   * Obtém a instância do processador de fundo virtual.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @returns {Object|null} Processador de fundo virtual ou null se falhar.
   * @async
   */
  async function getVirtualBackgroundProcessorInstance(channelParameters) {
    if (!virtualBackgroundProcessor && channelParameters.localVideoTrack) {
      virtualBackgroundProcessor = virtualBackgroundExtension.createProcessor()
      try {
        await virtualBackgroundProcessor.init()
      } catch (e) {
        console.log('Falha ao carregar o recurso WASM!')
        return null
      }
      channelParameters.localVideoTrack
        .pipe(virtualBackgroundProcessor)
        .pipe(channelParameters.localVideoTrack.processorDestination)
    }
    return virtualBackgroundProcessor
  }

  /**
   * Define uma cor sólida como fundo virtual.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @async
   */
  async function setBackgroundColor(channelParameters) {
    if (channelParameters.localVideoTrack) {
      let vbProcessor = await getVirtualBackgroundProcessorInstance(channelParameters)
      vbProcessor.setOptions({ type: 'color', color: '#F0DFD0' })
      await vbProcessor.enable()
    }
  }

  /**
   * Define o efeito de desfoque como fundo virtual.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @async
   */
  async function setBackgroundBlurring(channelParameters) {
    if (channelParameters.localVideoTrack) {
      let vbProcessor = await getVirtualBackgroundProcessorInstance(channelParameters)
      vbProcessor.setOptions({ type: 'blur', blurDegree: 2 }) // 1 - baixo, 2 - médio, 3 - alto
      await vbProcessor.enable()
    }
  }

  /**
   * Define uma imagem como fundo virtual.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @async
   */
  async function setBackgroundImage(channelParameters) {
    const imgElement = document.createElement('img')
    imgElement.onload = async () => {
      let vbProcessor = await getVirtualBackgroundProcessorInstance(channelParameters)
      vbProcessor.setOptions({ type: 'img', source: imgElement })
      await vbProcessor.enable()
    }
    imgElement.src = './background.jpg'
  }

  /**
   * Desativa o fundo virtual.
   * @param {Object} channelParameters - Parâmetros do canal.
   * @async
   */
  async function disableBackground(channelParameters) {
    let vbProcessor = await getVirtualBackgroundProcessorInstance(channelParameters)
    vbProcessor.disable()
  }

  return {
    getAgoraEngine,
    config,
    join,
    leave,
    setBackgroundColor,
    setBackgroundBlurring,
    setBackgroundImage,
    disableBackground
  }
}

export default AgoraManager